import { Injectable, signal, computed, effect, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { SocketNotification, SocketService } from '../socket/socket.service';
import { HtmlProcessingService } from '../html-processing/html-processing.service';
import { ApiService } from '../API/api-service';
import { n, r } from '@angular/cdk/overlay-module.d-CVO-IcaN';

export interface Notification {
  id: string;
  user: {
    name: string;
    avatar: string;
    _id?: string;
    email?: string;
  };
  title: string;
  project: string;
  description: string;
  timestamp: string;
  dueDate?: string;
  isRead: boolean;
  isPinned?: boolean;
  createdAt?: string;
  category?: 'general' | 'timesheet';
  // Object name for popup display
  objectname?: string;
  // Socket notification specific fields
  message?: string;
  isSeen?: boolean;
  status?: string;
  objID?: string;
  objCode?: string;
  notifyFromID?: string;
  notificationType?: string;
  notifyToObjCode?: string;
  notifyToObjID?: string;
  updateID?: string;
  updateReplayID?: string;
  comment?: string;
  // Object details for different objCode types
  objectDetails?: {
    name?: string;
    title?: string;
    _id?: string;
    status?: string;
    // For TASK
    taskName?: string;
    // For PROJECT
    projectName?: string;
    // For REVISION
    revisionName?: string;
    // For REQUEST
    requestName?: string;
    // For UPDATES
    updateName?: string;
    // For ISSUE
    issueName?: string;
    // For PEOPLE
    personName?: string;
    // For TIMESHEET
    timesheetName?: string;
    // Generic fields
    description?: string;
    type?: string;
  };
}

export interface PopupNotification extends Notification {
  showPopup: boolean;
  popupTimeout?: any;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService implements OnDestroy {
  private destroy$ = new Subject<void>();
  // Signal-based state management
  private notificationsSignal = signal<Notification[]>([]);
  private popupNotificationsSignal = signal<PopupNotification[]>([]);
  private isPanelOpenSignal = signal<boolean>(false);
  private isLoadingSignal = signal<boolean>(false);
  private isInitializedSignal = signal<boolean>(false);
  private isLoadingMoreSignal = signal<boolean>(false);
  private currentPageSignal = signal<number>(1);
  private hasMorePagesSignal = signal<boolean>(true);
  private totalPagesSignal = signal<number>(0);
  private htmlProcessingService = inject(HtmlProcessingService);

  // Public computed signals
  public notifications = computed(() => this.notificationsSignal());
  public popupNotifications = computed(() => this.popupNotificationsSignal());
  public unreadCount = computed(() => 
    this.notificationsSignal().filter(n => !n.isRead).length
  );
  public unseenCount = computed(() => 
    this.notificationsSignal().filter(n => !n.isSeen).length
  );
  public isLoading = computed(() => this.isLoadingSignal());
  public isInitialized = computed(() => this.isInitializedSignal());
  public isLoadingMore = computed(() => this.isLoadingMoreSignal());
  public currentPage = computed(() => this.currentPageSignal());
  public hasMorePages = computed(() => this.hasMorePagesSignal());
  public totalPages = computed(() => this.totalPagesSignal());

  // Subject for new notification events
  private newNotificationSubject = new Subject<Notification>();
  public newNotification$ = this.newNotificationSubject.asObservable();

  // Configuration
  private readonly POPUP_DURATION = 10000; // 8 seconds for testing
  private readonly MAX_POPUP_STACK = 3; // Maximum popups shown at once

  constructor(private socketService: SocketService,private apiService: ApiService) {
    // Set up effect to handle popup notifications when panel is open
    // effect(() => {
    //   const isPanelOpen = this.isPanelOpenSignal();
    // });
    this.setupSocketListeners();
    this.initializeNotifications();
  }

  private initializeNotifications(): void {
    if (this.isInitializedSignal()) {
      return;
    }

    this.isLoadingSignal.set(true);
    this.loadNotifications(1, true);
  }

  private loadNotifications(pageNo: number, isInitialLoad: boolean = false): void {
    if (isInitialLoad) {
      this.isLoadingSignal.set(true);
    } else {
      this.isLoadingMoreSignal.set(true);
    }

    this.apiService.getNotifications(pageNo)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          let apiNotifications: Notification[] = [];
          
          if (response && response.data && Array.isArray(response.data)) {
            apiNotifications = response.data.map((apiNotif: any) => this.mapApiNotificationToNotification(apiNotif));
            
            // Handle pagination metadata
            if (response.pagination) {
              this.totalPagesSignal.set(response.pagination.totalPages || 0);
              this.hasMorePagesSignal.set(response.pagination.hasMore || false);
            }
          } else if (response && Array.isArray(response)) {
            apiNotifications = response.map((apiNotif: any) => this.mapApiNotificationToNotification(apiNotif));
          }

          if (isInitialLoad) {
            this.notificationsSignal.set(apiNotifications);
            this.currentPageSignal.set(pageNo);
            this.isInitializedSignal.set(true);
            this.isLoadingSignal.set(false);
          } else {
            // Append new notifications to existing ones
            const currentNotifications = this.notificationsSignal();
            const updatedNotifications = [...currentNotifications, ...apiNotifications];
            this.notificationsSignal.set(updatedNotifications);
            this.currentPageSignal.set(pageNo);
            this.isLoadingMoreSignal.set(false);
          }
        },
        error: (error) => {
          if (isInitialLoad) {
            this.isLoadingSignal.set(false);
            this.isInitializedSignal.set(true); // Mark as initialized even on error to prevent retries
          } else {
            this.isLoadingMoreSignal.set(false);
          }
        }
      });
  }

  private mapObjectDetails(apiNotif: any): any {
    const objCode = apiNotif.objCode;
    
    switch (objCode) {
      case 'TASK':
        return apiNotif.taskDetails ? {
          name: apiNotif.taskDetails.taskName,
          title: apiNotif.taskDetails.taskName,
          _id: apiNotif.taskDetails._id,
          status: apiNotif.taskDetails.status,
          taskName: apiNotif.taskDetails.taskName,
          type: 'TASK'
        } : undefined;
        
      case 'PROJECT':
        return apiNotif.projectDetails ? {
          name: apiNotif.projectDetails.projectName || apiNotif.projectDetails.name,
          title: apiNotif.projectDetails.projectName || apiNotif.projectDetails.name,
          _id: apiNotif.projectDetails._id,
          status: apiNotif.projectDetails.status,
          projectName: apiNotif.projectDetails.projectName || apiNotif.projectDetails.name,
          type: 'PROJECT'
        } : undefined;
        
      case 'PEOPLE':
        return apiNotif.personDetails ? {
          name: apiNotif.personDetails.name,
          title: apiNotif.personDetails.name,
          _id: apiNotif.personDetails._id,
          status: apiNotif.personDetails.status,
          personName: apiNotif.personDetails.name,
          type: 'PEOPLE'
        } : undefined;
        
      case 'TIMESHEET':
        return apiNotif.timesheetDetails ? {
          name: apiNotif.timesheetDetails.timesheetName || apiNotif.timesheetDetails.name,
          title: apiNotif.timesheetDetails.timesheetName || apiNotif.timesheetDetails.name,
          _id: apiNotif.timesheetDetails._id,
          status: apiNotif.timesheetDetails.status,
          timesheetName: apiNotif.timesheetDetails.timesheetName || apiNotif.timesheetDetails.name,
          type: 'TIMESHEET'
        } : undefined;
        
      case 'REVISION':
        return apiNotif.revisionDetails ? {
          name: apiNotif.revisionDetails.revisionName || apiNotif.revisionDetails.name || apiNotif.revisionDetails.title,
          title: apiNotif.revisionDetails.revisionName || apiNotif.revisionDetails.name || apiNotif.revisionDetails.title,
          _id: apiNotif.revisionDetails._id,
          status: apiNotif.revisionDetails.status,
          revisionName: apiNotif.revisionDetails.revisionName || apiNotif.revisionDetails.name,
          type: 'REVISION'
        } : undefined;
        
      case 'REQUEST':
        return apiNotif.requestDetails ? {
          name: apiNotif.requestDetails.requestName || apiNotif.requestDetails.title || apiNotif.requestDetails.name,
          title: apiNotif.requestDetails.requestName || apiNotif.requestDetails.title || apiNotif.requestDetails.name,
          _id: apiNotif.requestDetails._id,
          status: apiNotif.requestDetails.status,
          requestName: apiNotif.requestDetails.requestName || apiNotif.requestDetails.title,
          type: 'REQUEST'
        } : undefined;
        
      case 'UPDATES':
        return apiNotif.updateDetails ? {
          name: apiNotif.updateDetails.updateName || apiNotif.updateDetails.title || apiNotif.updateDetails.name,
          title: apiNotif.updateDetails.updateName || apiNotif.updateDetails.title || apiNotif.updateDetails.name,
          _id: apiNotif.updateDetails._id,
          status: apiNotif.updateDetails.status,
          updateName: apiNotif.updateDetails.updateName || apiNotif.updateDetails.title || apiNotif.updateDetails.name,
          type: 'UPDATES'
        } : undefined;
        
      case 'ISSUE':
        return apiNotif.issueDetails ? {
          name: apiNotif.issueDetails.issueName || apiNotif.issueDetails.title || apiNotif.issueDetails.name,
          title: apiNotif.issueDetails.issueName || apiNotif.issueDetails.title || apiNotif.issueDetails.name,
          _id: apiNotif.issueDetails._id,
          status: apiNotif.issueDetails.status,
          issueName: apiNotif.issueDetails.issueName || apiNotif.issueDetails.title,
          type: 'ISSUE'
        } : undefined;
        
      default:
        // For any other objCode, try to find a generic name field
        const details = apiNotif[`${objCode.toLowerCase()}Details`] || apiNotif.details;
        return details ? {
          name: details.name || details.title || objCode,
          title: details.name || details.title || objCode,
          _id: details._id,
          status: details.status,
          type: objCode
        } : {
          name: objCode,
          title: objCode,
          type: objCode
        };
    }
  }

  private mapApiNotificationToNotification(apiNotif: any): Notification {
    // Map object details first to use for project field
    const objectDetails = this.mapObjectDetails(apiNotif);
    
    // Determine project name from objectDetails or fallback to objCode
    let projectName = apiNotif.project || '';
    if (objectDetails) {
      if (apiNotif.objCode === 'PROJECT' && objectDetails.projectName) {
        projectName = objectDetails.projectName;
      } else if (apiNotif.objCode === 'TASK' && objectDetails.taskName) {
        projectName = objectDetails.taskName;
      }  else if (apiNotif.objCode === 'TIMESHEET' && objectDetails.timesheetName) {
        projectName = objectDetails.timesheetName;
      } else if (apiNotif.objCode === 'REVISION' && objectDetails.revisionName) {
        projectName = objectDetails.revisionName;
      } else if (apiNotif.objCode === 'REQUEST' && objectDetails.requestName) {
        projectName = objectDetails.requestName;
      } else if (apiNotif.objCode === 'UPDATES' && objectDetails.updateName) {
        projectName = objectDetails.updateName;
      } else if (apiNotif.objCode === 'ISSUE' && objectDetails.issueName) {
        projectName = objectDetails.issueName;
      }
      else if (apiNotif.objCode === 'PEOPLE' && objectDetails.personName) {
        projectName = objectDetails.personName;
      } else if (apiNotif.objCode === 'TIMESHEET' && objectDetails.timesheetName) {
        projectName = objectDetails.timesheetName;
      } else if (objectDetails.name) {
        projectName = objectDetails.name;
      } else if (objectDetails.title) {
        projectName = objectDetails.title;
      }
    }
    
    // Fallback to objCode if no name found
    if (!projectName) {
      projectName = apiNotif.objCode || '';
    }
    
    return {
      id: apiNotif._id || apiNotif.id || '',
      user: {
        name: apiNotif.userDetails?.name || apiNotif.notifiedUserDetails?.name || 'Unknown User',
        avatar: apiNotif.userDetails?.image || apiNotif.notifiedUserDetails?.image || '',
        _id: apiNotif.userDetails?._id || apiNotif.user?._id,
        email: apiNotif.userDetails?.email || apiNotif.user?.email
      },
      title: apiNotif.message || apiNotif.title || 'Notification',
      project: projectName,
      description: apiNotif.comment || apiNotif.description || '',
      timestamp: this.formatTimestamp(apiNotif.createdAt || apiNotif.timestamp),
      dueDate: apiNotif.dueDate,
      isRead: apiNotif.isRead || false,
      isPinned: apiNotif.isPinned || false,
      createdAt: apiNotif.createdAt || new Date().toISOString(),
      category: apiNotif.category || (apiNotif.objCode === 'timesheet' ? 'timesheet' : 'general'),
      // Socket notification specific fields
      message: apiNotif.message,
      isSeen: apiNotif.isSeen,
      status: apiNotif.status,
      objID: apiNotif.objID,
      objCode: apiNotif.objCode,
      notifyFromID: apiNotif.notifyFromID,
      notificationType: apiNotif.notificationType,
      notifyToObjCode: apiNotif.notifyToObjCode,
      notifyToObjID: apiNotif.notifyToObjID,
      updateID: apiNotif.updateID,
      updateReplayID: apiNotif.updateReplayID,
      comment: apiNotif.comment,
      // Object details - dynamically map based on objCode
      objectDetails: objectDetails
    };
  }

  setPanelOpen(isOpen: boolean): void {
    this.isPanelOpenSignal.set(isOpen);
  }

  isPanelOpen(): boolean {
    return this.isPanelOpenSignal();
  }


   /**
   * Add a new notification with a specific ID (for socket notifications)
   */
   addNotificationWithId(notification: Omit<Notification, 'id' | 'createdAt'>, id: string): void {
    const newNotification: Notification = {
      ...notification,
      id: id, // Use the provided ID from socket response
      createdAt: new Date().toISOString(),
      category: notification.category ?? 'general'
    };

    // Add to notifications list
    const currentNotifications = this.notificationsSignal();
    this.notificationsSignal.set([newNotification, ...currentNotifications]);

    // Emit new notification event
    this.newNotificationSubject.next(newNotification);

    
      this.showPopupNotification(newNotification);
  

  }

  refreshNotifications(): void {
    this.isLoadingSignal.set(true);
    this.currentPageSignal.set(1);
    this.hasMorePagesSignal.set(true);
    this.loadNotifications(1, true);
  }

  loadMoreNotifications(): void {
    if (this.isLoadingMoreSignal() || !this.hasMorePagesSignal()) {
      return;
    }

    const nextPage = this.currentPageSignal() + 1;
    this.loadNotifications(nextPage, false);
  }

  canLoadMore(): boolean {
    return this.hasMorePagesSignal() && !this.isLoadingMoreSignal();
  }
  /**
   * Show popup notification
   */
  private showPopupNotification(notification: Notification): void {
    const currentPopups = this.popupNotificationsSignal();

    // Limit maximum number of popups
    if (currentPopups.length >= this.MAX_POPUP_STACK) {
      // Remove oldest popup
      const updatedPopups = currentPopups.slice(1);
      this.popupNotificationsSignal.set(updatedPopups);
    }

    // Create popup notification
    const popupNotification: PopupNotification = {
      ...notification,
      showPopup: true,
      popupTimeout: this.POPUP_DURATION
    };

    // Add to popup stack
    this.popupNotificationsSignal.set([...this.popupNotificationsSignal(), popupNotification]);
    // Auto-dismiss after duration
    const timeout = setTimeout(() => {
      this.dismissPopup(notification.id);
    }, this.POPUP_DURATION);
  }

  /**
   * Dismiss popup notification
   */
  dismissPopup(notificationId: string): void {
    const currentPopups = this.popupNotificationsSignal();
    const popup = currentPopups.find(p => p.id === notificationId);
    
    if (popup?.popupTimeout) {
      clearTimeout(popup.popupTimeout);
    }

    const updatedPopups = currentPopups.filter(p => p.id !== notificationId);
    this.popupNotificationsSignal.set(updatedPopups);
  }

  /**
   * Mark notification as read
   */
  markAsRead(notificationId: string): void {
    const currentNotifications = this.notificationsSignal();
    const updatedNotifications = currentNotifications.map(n => 
      n.id === notificationId ? { ...n, isRead: true } : n
    );
    this.notificationsSignal.set(updatedNotifications);
    this.apiService.markNotificationAsRead(notificationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
        },
        error: (error) => {
          // Revert the local state if API call fails
          this.notificationsSignal.set(currentNotifications);
        }
      });
    this.socketService.markNotificationAsRead(notificationId);
  }

  markAsSeen(notificationId: string): void {
    const currentNotifications = this.notificationsSignal();
    const updatedNotifications = currentNotifications.map(n => 
      n.id === notificationId ? { ...n, isSeen: true } : n
    );
    this.notificationsSignal.set(updatedNotifications);
    this.apiService.markNotificationAsSeen(notificationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
        },
        error: (error) => {
          // Revert the local state if API call fails
          this.notificationsSignal.set(currentNotifications);
        }
      });
  }

  markAllAsRead(): void {
    const currentNotifications = this.notificationsSignal();
    const updatedNotifications = currentNotifications.map(n => ({ ...n, isRead: true}));
    this.notificationsSignal.set(updatedNotifications);
    
    this.apiService.markAllNotificationsAsRead()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
        },
        error: (error) => {
          // Revert the local state if API call fails
          this.notificationsSignal.set(currentNotifications);
        }
      });
  }

  markAllAsSeen(): void {
    const currentNotifications = this.notificationsSignal();
    const updatedNotifications = currentNotifications.map(n => ({ ...n, isSeen: true }));
    this.notificationsSignal.set(updatedNotifications);
    
    this.apiService.markAllNotificationsAsSeen()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
        },
        error: (error) => {
          // Revert the local state if API call fails
          this.notificationsSignal.set(currentNotifications);
        }
      });
  }

  /**
   * Toggle pin status
   */
  togglePin(notificationId: string): void {
    const currentNotifications = this.notificationsSignal();
    const notification = currentNotifications.find(n => n.id === notificationId);
    const updatedNotifications = currentNotifications.map(n => 
      n.id === notificationId ? { ...n, isPinned: !n.isPinned } : n
    );
    this.notificationsSignal.set(updatedNotifications);
    // Emit socket event to server
    if (notification) {
      this.socketService.toggleNotificationPin(notificationId, !notification.isPinned);
    }
  }

  /**
   * Clear all notifications
   */
  clearAll(): void {
    const currentNotifications = this.notificationsSignal();
    const updatedNotifications = currentNotifications.map(n => ({ ...n, isRead: true }));
    this.notificationsSignal.set(updatedNotifications);
  }

  /**
   * Clear all popups
   */
  clearAllPopups(): void {
    const currentPopups = this.popupNotificationsSignal();
    currentPopups.forEach(popup => {
      if (popup.popupTimeout) {
        clearTimeout(popup.popupTimeout);
      }
    });
    this.popupNotificationsSignal.set([]);
  }

  /**
   * Generate unique ID
   */
  private generateId(): string {
    return `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Format timestamp to relative time
   */
  private formatTimestamp(createdAt: string): string {
    const now = new Date();
    const notificationDate = new Date(createdAt);
  
    // difference in seconds (based on absolute epoch ms) - fine for "Just now"
    const diffInSeconds = Math.floor((now.getTime() - notificationDate.getTime()) / 1000);
    if (diffInSeconds < 60) return 'Just now';
  
    // Formatters that use Asia/Kolkata (IST) timezone — do NOT add offsets manually
    const timeFormatter = new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  
    const fullFormatter = new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  
    // Helper to get yyyy-mm-dd string in IST for comparison (using formatToParts)
    const datePartsFormatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  
    const partsFor = (d: Date) => {
      const parts = datePartsFormatter.formatToParts(d);
      const y = parts.find(p => p.type === 'year')!.value;
      const m = parts.find(p => p.type === 'month')!.value;
      const day = parts.find(p => p.type === 'day')!.value;
      return `${y}-${m}-${day}`; // e.g. "2025-10-30"
    };
  
    const nowDateKey = partsFor(now);
    const notifDateKey = partsFor(notificationDate);
  
    // Build yesterday's date key (in IST): derive from now in IST by constructing a Date
    // using the IST date string to avoid manual offsets:
    const nowISTString = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(now); // "dd/mm/yyyy" or "yyyy-mm-dd" depending locale, but we used formatToParts above for keys
    // Simpler: create a Date representing midnight IST for 'now' using formatToParts results:
    const nowParts = datePartsFormatter.formatToParts(now);
    const nowY = Number(nowParts.find(p => p.type === 'year')!.value);
    const nowM = Number(nowParts.find(p => p.type === 'month')!.value) - 1; // zero-indexed month
    const nowD = Number(nowParts.find(p => p.type === 'day')!.value);
    // Create a Date that corresponds to midnight IST by using Date.UTC for that Y/M/D and then subtracting the IST offset:
    // but we only need yesterday key — easiest: create Date for now, then shift by -1 day and compare parts
    const yesterday = new Date(now);
    // move local time by -1 day (we're only using partsFormatter with timeZone 'Asia/Kolkata' for keys)
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayKey = partsFor(yesterday);
  
    const timeString = timeFormatter.format(notificationDate); // properly in IST
  
    if (notifDateKey === nowDateKey) {
      return `Today, ${timeString}`;
    } else if (notifDateKey === yesterdayKey) {
      return `Yesterday, ${timeString}`;
    } else {
      return fullFormatter.format(notificationDate); // e.g. "Thu, Oct 24, 10:30 AM" in IST
    }
  }
  
  
  private setupSocketListeners(): void {
    // Listen for new notifications from socket
    this.socketService.newNotification$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (socketNotification: SocketNotification) => {
          try {
            // Convert socket notification to our notification format
            const notificationData = socketNotification.data;
            
            // Support both new structure (notificationDetails) and legacy structure (createdNotification)
            const notificationDetails = notificationData.notificationDetails || notificationData.createdNotification;
            // Support both new structure (toUserDetails) and legacy structure (notifyToDetails)
            const toUserDetails = notificationData.toUserDetails || notificationData.notifyToDetails;
            // Support both new structure (userDetails) and legacy structure (user)
            const userDetails = notificationData.userDetails || notificationData.user;
            // Support both new structure (objdata) and legacy structure (objData)
            const objData = notificationData.objdata || notificationData.objData;
            
            // Validate required data
            if (!userDetails || !userDetails.name) {
              return;
            }
            
            if (!notificationDetails || !notificationDetails.message) {
              return;
            }
            
            const objectname = this.getObjectNameFromObjData(objData, notificationDetails.objCode);
            
            const notification: Omit<Notification, 'id' | 'createdAt'> = {
              user: {
                name: userDetails.name,
                avatar: userDetails.image || '',
                _id: userDetails._id,
                email: userDetails.email
              },
              title: notificationDetails.message,
              project: this.getProjectNameFromObjData(objData, notificationDetails.objCode),
              // description: this.htmlProcessingService.stripHtmlTags(notificationDetails.comment || ''),
              description: notificationDetails.comment || '',
              timestamp: this.formatTimestamp(notificationDetails.createdAt),
              isRead: notificationDetails.isRead,
              isPinned: false,
              category: 'general',
              // Object name for popup display
              objectname: objectname,
              // Socket notification specific fields
              message: notificationDetails.message,
              isSeen: notificationDetails.isSeen,
              status: notificationDetails.status,
              objID: notificationDetails.objID,
              objCode: notificationDetails.objCode,
              notifyFromID: notificationDetails.notifyFromID,
              notificationType: notificationDetails.notificationType,
              notifyToObjCode: notificationDetails.notifyToObjCode,
              notifyToObjID: notificationDetails.notifyToObjID,
              updateID: notificationDetails.updateID,
              updateReplayID: notificationDetails.updateReplayID || undefined,
              comment: notificationDetails.comment,
              // Object details for different objCode types
              objectDetails: this.mapSocketObjectDetails(objData, notificationDetails.objCode)
            };

            // Add the notification with the real _id from socket response
            this.addNotificationWithId(notification, notificationDetails._id);
          } catch (error) {
            console.error('[NotificationService] Error processing socket notification:', error);
          }
        },
        error: (error) => {
          console.error('[NotificationService] Error in socket notification stream:', error);
        }
      });
  }


 

  /**
   * Get project name from objData based on objCode
   */
  private getProjectNameFromObjData(objData: any, objCode: string): string {
    if (!objData) {
      return objCode;
    }
    
    switch (objCode) {
      case 'TASK':
        return objData.taskName || objData.name || 'Task';
      case 'PROJECT':
        return objData.projectName || objData.name || 'Project';
      case 'ISSUE':
        return objData.issueName || objData.title || 'Issue';
      case 'REQUEST':
        return objData.requestName || objData.title || 'Request';
      case 'REVISION':
        return objData.revisionName || objData.name || 'Revision';
      case 'PEOPLE':
        return objData.name || 'Person';
      case 'TIMESHEET':
        return objData.name || objData.title || 'Timesheet';
      default:
        return objData.name || objData.title || objData.taskName || objData.projectName || objCode;
    }
  }

  /**
   * Get object name from objData based on objCode (for popup display)
   */
  private getObjectNameFromObjData(objData: any, objCode: string): string {
    if (!objData) {
      return objCode;
    }
    
    switch (objCode) {
      case 'TASK':
        return objData.taskName || objData.name || 'Task';
      case 'PROJECT':
        return objData.projectName || objData.name || 'Project';
      case 'ISSUE':
        return objData.issueName || objData.title || 'Issue';
      case 'REQUEST':
        return objData.requestName || objData.title || 'Request';
      case 'REVISION':
        return objData.revisionName || objData.name || 'Revision';
      case 'PEOPLE':
        return objData.name || 'Person';
      case 'TIMESHEET':
        return objData.name || objData.title || 'Timesheet';
      default:
        return objData.name || objData.title || objData.taskName || objData.projectName || objCode;
    }
  }

  /**
   * Map socket object details for different objCode types
   */
  private mapSocketObjectDetails(objData: any, objCode: string): any {
    if (!objData) {
      return {
        name: objCode,
        title: objCode,
        type: objCode
      };
    }
    
    switch (objCode) {
      case 'TASK':
        return {
          name: objData.taskName || objData.name,
          title: objData.taskName || objData.name,
          _id: objData._id,
          status: objData.status,
          taskName: objData.taskName,
          description: objData.description,
          type: 'TASK'
        };
        
      case 'PROJECT':
        return {
          name: objData.projectName || objData.name,
          title: objData.projectName || objData.name,
          _id: objData._id,
          status: objData.status,
          projectName: objData.projectName || objData.name,
          description: objData.description,
          type: 'PROJECT'
        };
      case 'ISSUE':
        return {
          name: objData.issueName || objData.title,
          title: objData.issueName || objData.title,
        };
      case 'REQUEST':
        return {
          name: objData.requestName || objData.title,
          title: objData.requestName || objData.title,
        };
      case 'REVISION':
        return {
          name: objData.revisionName || objData.name,
          title: objData.revisionName || objData.name,
        };  
        
      case 'PEOPLE':
        return {
          name: objData.name,
          title: objData.name,
          _id: objData._id,
          status: objData.status,
          personName: objData.name,
          type: 'PEOPLE'
        };
        
      case 'TIMESHEET':
        return {
          name: objData.name || objData.title,
          title: objData.name || objData.title,
          _id: objData._id,
          status: objData.status,
          timesheetName: objData.name || objData.title,
          description: objData.description,
          type: 'TIMESHEET'
        };
        
      default:
        return {
          name: objData.name || objData.title || objData.taskName || objData.projectName || objCode,
          title: objData.name || objData.title || objData.taskName || objData.projectName || objCode,
          _id: objData._id,
          status: objData.status,
          description: objData.description,
          type: objCode
        };
    }
  }

    /**
   * Get route path based on notification object details
   */
    getNotificationRoute(notification: Notification): string[] | null {
      if (!notification.objCode) {
        return null;
      }
  
      // Try to get ID from objID first, then fallback to objectDetails._id
      const objID = notification.objID || notification.objectDetails?._id;
      
      if (!objID) {
        return null;
      }
  
      const objCode = notification.objCode;
  
      switch (objCode) {
        case 'TASK':
          // Check if this is an update notification - route to updates tab
          if (notification.updateID || 
              notification.notificationType?.toLowerCase().includes('update') ||
              (notification.notifyToObjCode === 'TASK' && notification.updateID)) {
            return ['/task', 'task-details', objID, 'updates'];
          }
          return ['/task', 'task-details', objID];
        
        case 'PROJECT':
          // Check if this is an update notification - route to updates tab
          // An update notification can be identified by:
          // 1. Having an updateID
          // 2. Having notificationType that includes 'update'
          // 3. Having notifyToObjCode === 'PROJECT' with an updateID (update on a project)
          if (notification.updateID || 
              notification.notificationType?.toLowerCase().includes('update') ||
              (notification.notifyToObjCode === 'PROJECT' && notification.updateID)) {
            return ['/projects', 'project-details', objID, 'updates'];
          }
          return ['/projects', 'project-details', objID];
        
        case 'ISSUE':
          // Check if this is an update notification - route to updates tab
          if (notification.updateID || 
              notification.notificationType?.toLowerCase().includes('update') ||
              (notification.notifyToObjCode === 'ISSUE' && notification.updateID)) {
            return ['/issue', 'issue-details', objID, 'updates'];
          }
          return ['/issue', 'issue-details', objID];
        
        case 'REQUEST':
          // Check if this is an update notification - route to updates tab
          if (notification.updateID || 
              notification.notificationType?.toLowerCase().includes('update') ||
              (notification.notifyToObjCode === 'REQUEST' && notification.updateID)) {
            return ['/request', 'request-details', objID, 'updates'];
          }
          return ['/request', 'request-details', objID];
        
        case 'REVISION':
          // Check if this is an update notification - route to updates tab
          if (notification.updateID || 
              notification.notificationType?.toLowerCase().includes('update') ||
              (notification.notifyToObjCode === 'REVISION' && notification.updateID)) {
            return ['/revision', 'revision-details', objID, 'updates'];
          }
          return ['/revision', 'revision-details', objID];
        
        case 'TIMESHEET':
          // Timesheet might not have a detail page, so route to listing
          return ['/timesheets'];
        
        case 'PEOPLE':
          return ['/people', 'peopleview', objID];
        
        case 'UPDATES':
          if (notification.notifyToObjCode && notification.notifyToObjID) {
            const parentObjCode = notification.notifyToObjCode;
            const parentObjID = notification.notifyToObjID;
            
            // Route to parent object's updates page
            switch (parentObjCode) {
              case 'TASK':
                return ['/task', 'task-details', parentObjID, 'updates'];
              case 'PROJECT':
                return ['/projects', 'project-details', parentObjID, 'updates'];
              case 'ISSUE':
                return ['/issue', 'issue-details', parentObjID, 'updates'];
              case 'REVISION':
                return ['/revision', 'revision-details', parentObjID, 'updates'];
              case 'REQUEST':
                return ['/request', 'request-details', parentObjID, 'updates'];
              default:
                // Fallback to general updates page
                return ['/updates'];
            }
          }
          // If no parent object info, route to general updates listing
          return ['/updates'];
        
        default:
          return null;
      }
    }

    /**
     * Get fragment (hash) for scrolling to specific update/reply
     * Returns fragment in format: 'update-{updateID}' or 'reply-{replyID}'
     */
    getNotificationFragment(notification: Notification): string | null {
      // Check if this is a reply notification
      if (notification.updateReplayID) {
        return `reply-${notification.updateReplayID}`;
      }
      
      // Check if this is an update notification
      if (notification.updateID) {
        return `update-${notification.updateID}`;
      }
      
      return null;
    }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
